<template>
  <div class="makercrate-section">
    <div class="makercrate-items-container">
      <MakercrateFilterSection
        :selected-product="selectedProduct"
        :selected-locale="selectedLocale"
        @update:selected-product="selectedProduct = $event"
        @update:selected-locale="onLocaleSelectChange"
      />

      <div v-if="isLoading" class="loading-state">
        <div class="spinner-container">
          <div class="mt-5 spinner"></div>
        </div>
      </div>

      <div v-else>
        <div class="makercrate-item-list">
          <div v-if="filteredItems.length === 0" class="no-items-message">
            <ErrorState
              state="notFound"
              title="No Makercrates Found"
              message="No makercrates match your selected filters."
            />
          </div>
          <div v-else class="makercrate-item-grid-container">
            <MakercrateSectionItem
              v-for="item in filteredItems"
              :key="item.id"
              :item="item"
              @open-detail="handleOpenDetail"
            />
          </div>
        </div>
      </div>

      <!-- Makercrate Detail Modal -->
      <MakercrateDetailModal
        :visible="showModal"
        :makercart-id="selectedMakercrateId"
        @close="closeModal"
      />
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from "vue";
import MakercrateSectionItem from "./MakercrateSectionItem.vue";
import MakercrateFilterSection from "./MakercrateFilterSection.vue";
import MakercrateDetailModal from "./MakercrateDetailModal.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import makercrateService from "@/services/makercrateService";
import { useRoute, useRouter } from "vue-router";

const props = defineProps({
  activeLocale: {
    type: String,
    default: "US",
  },
});

const emit = defineEmits(["update:selectedLocale", "locale-changed"]);

const selectedProduct = ref("all");
const selectedLocale = ref(props.activeLocale || "US");
const isLoading = ref(false);

const router = useRouter();
const route = useRoute();

const showModal = ref(false);
const selectedMakercrateId = ref(null);

const onLocaleSelectChange = (locale) => {
  selectedLocale.value = locale;
  emit("update:selectedLocale", locale);
  emit("locale-changed", locale);
};

const mapMakercrateToItem = (crate) => {
  const images = crate.images || [];
  const primaryImage =
    images[0]?.thumbnails?.large?.url ||
    images[0]?.url ||
    "/images/content-placeholder.svg";
  const secondaryImage =
    images[1]?.thumbnails?.large?.url ||
    images[1]?.url ||
    "/images/content-placeholder.svg";

  return {
    id: crate.id,
    title: crate.title,
    price: crate.price,
    subheader: crate.subheader || "",
    description: crate.description || "",
    hasMakeyBadge: !!crate.hardware,
    primaryImage,
    secondaryImage,
  };
};

const makercrateItems = ref([]);

const fetchMakercrates = async () => {
  isLoading.value = true;
  try {
    const response = await makercrateService.getMakercrates({
      sort_dir: "asc",
    });
    makercrateItems.value = (response?.data?.rows || []).map(
      mapMakercrateToItem
    );
  } catch (error) {
    console.error("Failed to fetch makercrates:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchMakercrates();

  if (route.query.item) {
    handleOpenDetail(route.query.item);
  }
});

const filteredItems = computed(() => {
  if (selectedProduct.value === "all") {
    return makercrateItems.value;
  }
  return makercrateItems.value.filter((item) =>
    item.title.includes(selectedProduct.value)
  );
});

const handleOpenDetail = (id) => {
  selectedMakercrateId.value = id;
  if (id) {
    const query = { ...route.query };
    query.item = id;
    router.replace({ query });
  }
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedMakercrateId.value = null;

  const query = { ...route.query };
  delete query.item;
  router.replace({ query });
};
</script>

<style scoped>
.makercrate-section {
  width: 100%;
}

.makercrate-items-container {
  width: 100%;
  margin-top: 20px;
}

.makercrate-item-list {
  flex-wrap: wrap;
  padding: 20px 0px;
  min-height: 70vh;
  justify-content: center;
}

.makercrate-item-grid-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.no-items-message {
  text-align: center;
  width: 100%;
  padding: 20px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .makercrate-item-grid-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .makercrate-item-grid-container {
    grid-template-columns: 1fr;
  }

  .makercrate-items-container {
    margin-top: 16px;
  }

  .makercrate-item-list {
    padding: 0px;
  }
}

@media (max-width: 480px) {
  .makercrate-item-grid-container {
    gap: 30px;
  }
}
</style>
